import { financeNumber } from '../pendingInvoiceCost';
import api from '../../../services/api';
import type { PivotExportTable } from './pivotExport';

// Single home for the "Rachunek wyników" (income statement / EBITDA bridge)
// contract. Backend: GET /api/reports/income-statement — composed from
// reportService.getCompanyBalanceReport, same from/to (YYYY-MM) contract and
// 24-month cap as /company-balance. Profit at sale (decision
// 2026-10-29-zysk-przy-sprzedazy): the month lines are the ZK money of the
// `forecastVsActual` block (revenue − cost of goods sold); `purchases` is
// information only. See docs/modules/finance-and-margin.md and
// docs/decisions/2026-09-12-rachunek-wynikow-ebitda.md.

export type IncomeStatementBasis = 'forecast' | 'actual';

export interface IncomeStatementLine {
  revenue: number;
  // Cost of goods sold. An incomplete cost arrives as its known part with
  // `pendingCostOrderCount` > 0 (never null since 2026-10-29); `null` is kept
  // in the type for safety: then `ebitda`/`profit` and their percents are null too.
  orderCosts: number | null;
  margin: number | null;
  marginPercent: number | null;
  operatingFixedCosts: {
    total: number;
    byCategory: Record<string, number>;
    /** The same total by cost group (MPK); key `none` = „Bez grupy” (decision 2026-10-29). */
    byGroup?: Record<string, number>;
  };
  ebitda: number | null;
  ebitdaPercent: number | null;
  depreciation: number;
  financialCosts: number;
  // null only when `ebitda` is null (unknown margin) — see incomeTax below.
  preTaxResult: number | null;
  // Always numeric: the entered INCOME_TAX cost for the period, 0 when none.
  incomeTaxEntered: number;
  // 9% of a positive `preTaxResult`, 0 for a non-positive one, null when
  // `preTaxResult` is unknown — see docs/decisions/2026-09-12-rachunek-wynikow-ebitda.md.
  incomeTaxEstimated: number | null;
  // 'entered' when `incomeTaxEntered > 0` overrode the estimate, 'estimated'
  // otherwise (including when both are 0/unknown).
  incomeTaxSource: 'entered' | 'estimated';
  incomeTax: number | null;
  profit: number | null;
  profitPercent: number | null;
  // ZK whose cost in this line is only its known part (goods not issued yet,
  // a plan line without an average purchase cost).
  pendingCostOrderCount: number;
  // Forecast line: ZK of the part still waiting for an invoice with an
  // incomplete cost (a ZK in both parts counts once in pendingCostOrderCount).
  planPendingCostOrderCount?: number;
  // Actual line: `orderId|invoiceId` → change of pending invoice lines in the
  // month (nonzero only). A sum over months above zero = that ZK is still
  // incomplete in the sum (`pendingScope.ts`, one „*” rule for every range).
  pendingCostDeltas?: Record<string, number>;
}

export interface IncomeStatementMonth {
  month: string;
  isCurrent: boolean;
  isFuture: boolean;
  actual: IncomeStatementLine;
  forecast: IncomeStatementLine;
}

export type IncomeStatementReviewState =
  | 'INCLUDED'
  | 'PARTIAL'
  | 'READY'
  | 'REVIEW'
  | 'PLANNED'
  | 'EXCLUDED';

export interface IncomeStatementCustomerOrderMoney {
  revenue: number;
  orderCosts: number | null;
  margin: number | null;
}

export interface IncomeStatementCustomerOrder {
  id: string;
  orderNumber: string;
  displayOrderNumber: string;
  status: string;
  plannedDate: string | null;
  // 'YYYY-MM' delivery months of the in-period forecast (forecast by
  // delivery, docs/decisions/2026-10-16-prognoza-wedlug-dostaw.md); empty
  // when the order is in the period only by its invoice.
  forecastMonths: string[];
  reviewState: IncomeStatementReviewState;
  requiresReview: boolean;
  inActual: boolean;
  inForecast: boolean;
  // DELIVERED, in forecast, with no settlement entry at all yet (ever, not
  // just this period) — see docs/decisions/2026-09-21-rachunek-wynikow-nierozliczone-i-przeglad.md.
  awaitingSettlement: boolean;
  // The order's cost here is only its known part (2026-10-29).
  costPending: boolean;
  // The realized part alone has an incomplete cost.
  actualCostPending?: boolean;
  actual: IncomeStatementCustomerOrderMoney | null;
  forecast: IncomeStatementCustomerOrderMoney | null;
}

export interface IncomeStatementAwaitingSettlementSummary {
  orderCount: number;
  revenue: number;
}

export interface IncomeStatementOutsidePlanOrder {
  id: string;
  orderNumber: string;
  displayOrderNumber: string;
  customerName: string;
  plannedDate: string | null;
  reviewReasons: string[];
  revenue: number;
}

export interface IncomeStatementOutsidePlan {
  orderCount: number;
  revenue: number;
  orders: IncomeStatementOutsidePlanOrder[];
}

export interface IncomeStatementCustomerTotals {
  orderCount: number;
  revenue: number;
  orderCosts: number | null;
  margin: number | null;
  marginPercent: number | null;
  /** ZK of the customer counted with only a known part of their cost. */
  pendingCostOrderCount?: number;
}

export interface IncomeStatementCustomer {
  key: string;
  id: string | null;
  name: string;
  taxId: string | null;
  address: string | null;
  actual: IncomeStatementCustomerTotals;
  forecast: IncomeStatementCustomerTotals;
  orders: IncomeStatementCustomerOrder[];
}

export interface IncomeStatementPurchase {
  /** INVOICE / PLAN; WP2: CORRECTION and WITHDRAWAL carry a signed difference in their own month. */
  kind: 'INVOICE' | 'PLAN' | 'CORRECTION' | 'WITHDRAWAL';
  supplierOrderId: string; orderNumber: string; supplierName: string;
  invoiceId: string | null; documentNumber: string | null; month: string; actual: boolean;
  contractCurrency: string; contractNet: number; contractNetPLN: number; contractRate: number | null;
  invoiceCurrency: string | null; invoiceNet: number | null; invoiceRate: number | null;
  /** null only for a confirmation draft whose lines have no average purchase cost at all (SZ.9). */
  netPLN: number | null; extraCostPLN: number;
  /** SZ.9: a confirmation draft ZD without a supplier, priced at the average purchase cost. */
  draft: IncomeStatementPurchaseDraft | null;
}
export interface IncomeStatementPurchaseDraft {
  sourceOrderId: string | null;
  sourceOrderNumber: string | null;
  /** Some line has no average purchase cost: that part is not in the amount. */
  costPending: boolean;
  /** Why (WP5 reasons): NO_AVERAGE_COST | AVERAGE_UNIT_UNRESOLVED | AVERAGE_RATE_UNAVAILABLE. */
  pendingReasons: string[];
}
// WP6 "Prognoza a wynik" (docs/decisions/2026-09-24-wp6-prognoza-a-wynik.md).
// Customer orders are on the order-margin basis (WP5 plan / ledger COGS) and
// are the company result (2026-10-29); ZD purchases are information on the
// purchase basis; the two are never added together.
export interface ForecastVsActualOrderTotals {
  orderCount: number;
  revenue: number;
  // null = at least one order has an unknown cost (never coerced to 0).
  totalCost: number | null;
  margin: number | null;
  marginPercent: number | null;
  pendingCostOrderCount: number;
}

export interface ForecastVsActualPurchaseTotals {
  count: number;
  amountPLN: number;
  /** Rows summed with their known part only (SZ.9 drafts without an average). */
  pendingCostCount: number;
}

export interface ForecastVsActualBasis {
  orders: ForecastVsActualOrderTotals;
  purchases: ForecastVsActualPurchaseTotals;
}

export interface ForecastVsActualMonth {
  month: string;
  forecast: ForecastVsActualBasis;
  actual: ForecastVsActualBasis;
}

export interface ForecastVsActualMoney {
  revenue: number | null;
  totalCost: number | null;
  margin: number | null;
}

// Forecast by delivery: where a partial forecast row comes from.
export type ForecastDeliverySource = 'TRUCK' | 'NO_TRUCK' | 'MIXED';
const FORECAST_DELIVERY_SOURCES: unknown[] = ['TRUCK', 'NO_TRUCK', 'MIXED'];

export interface ForecastVsActualOrder {
  id: string;
  orderNumber: string;
  displayOrderNumber: string;
  status: string;
  customer: { id: string | null; name: string } | null;
  // „Handlowcy” (2026-10-29): the ZK's Opiekun (null = „Bez opiekuna”), the
  // manual flag „Opłacone” (PAID) and the payment mode. Identity, no money.
  // Always set by the normalizer; optional only for older fixtures.
  accountManager?: { id: string | null; name: string } | null;
  customerPaymentStatus?: 'PAID' | 'PENDING';
  paymentMode?: 'TERM' | 'PREPAYMENT' | 'FACTORING';
  // Forecast: the planned month; actual: the sales invoice recognition month.
  month: string | null;
  revenue: number;
  // With `costPending` the known part only (2026-10-29: never null from the
  // backend; null is kept for safety and means unknown, never zero).
  totalCost: number | null;
  margin: number | null;
  marginPercent: number | null;
  costPending: boolean;
  // Actual only: invoiceId → change of pending invoice lines in the month
  // (nonzero only); see `pendingScope.ts`.
  pendingDeltas?: Record<string, number>;
  // Forecast only: share of the order's remaining value delivered in this
  // month (forecast by delivery, 2026-10-16); null = the whole order.
  deliveryShare?: number | null;
  // Where that share comes from: trucks delivering in the month, the part on
  // no truck (kept in the ZK forecast month) or both; null = unknown.
  deliverySource?: ForecastDeliverySource | null;
  // Actual only: the already realized part of a pending ledger cost.
  knownCost: number | null;
  // Actual only: unissued goods of an invoiced order priced by the WP5 plan
  // at the average frozen at the invoice — never part of the actual cost.
  unissued: { averageCostPLN: number | null; costUnknown: boolean } | null;
  // Actual only: FIFO cost of goods issued beyond the invoice (question 2.8),
  // informational — never part of the actual cost, margin or totals.
  issuedOverInvoiceCostPLN: number | null;
  // Snapshot at the first confirmation; null when never confirmed.
  planned: (ForecastVsActualMoney & { snapshotAt: string | null }) | null;
  delta: ForecastVsActualMoney | null;
}

export interface ForecastVsActual {
  months: ForecastVsActualMonth[];
  totals: { forecast: ForecastVsActualBasis; actual: ForecastVsActualBasis };
  orders: { forecast: ForecastVsActualOrder[]; actual: ForecastVsActualOrder[] };
  // Period sum of issuedOverInvoiceCostPLN over the actual orders; not in totals.
  issuedOverInvoice: { orderCount: number; costPLN: number };
}

export interface IncomeStatementResponse {
  purchases?: IncomeStatementPurchase[];
  // WP6 block; absent for non-ADMIN readers (question 6.8) — null, not empty.
  forecastVsActual: ForecastVsActual | null;
  from: string;
  to: string;
  currentMonth: string;
  months: IncomeStatementMonth[];
  totals: { actual: IncomeStatementLine; forecast: IncomeStatementLine };
  customers: IncomeStatementCustomer[];
  awaitingSettlementSummary: IncomeStatementAwaitingSettlementSummary;
  outsidePlan: IncomeStatementOutsidePlan;
}

function record(value: unknown): Record<string, any> {
  return value && typeof value === 'object' ? value as Record<string, any> : {};
}

function num(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function text(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function bool(value: unknown): boolean {
  return value === true;
}

function nullableText(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

function normalizeByCategory(value: unknown): Record<string, number> {
  const source = record(value);
  return Object.fromEntries(Object.entries(source).map(([key, amount]) => [key, num(amount)]));
}

function normalizeDeltas(value: unknown): Record<string, number> {
  const source = record(value);
  return Object.fromEntries(Object.entries(source)
    .map(([key, delta]) => [key, num(delta)] as [string, number])
    .filter(([, delta]) => delta !== 0));
}

function normalizeLine(value: unknown): IncomeStatementLine {
  const source = record(value);
  const operatingFixedCosts = record(source.operatingFixedCosts);
  return {
    revenue: num(source.revenue),
    orderCosts: financeNumber(source.orderCosts),
    margin: financeNumber(source.margin),
    marginPercent: financeNumber(source.marginPercent),
    operatingFixedCosts: {
      total: num(operatingFixedCosts.total),
      byCategory: normalizeByCategory(operatingFixedCosts.byCategory),
      byGroup: normalizeByCategory(operatingFixedCosts.byGroup),
    },
    ebitda: financeNumber(source.ebitda),
    ebitdaPercent: financeNumber(source.ebitdaPercent),
    depreciation: num(source.depreciation),
    financialCosts: num(source.financialCosts),
    preTaxResult: financeNumber(source.preTaxResult),
    incomeTaxEntered: num(source.incomeTaxEntered),
    incomeTaxEstimated: financeNumber(source.incomeTaxEstimated),
    incomeTaxSource: source.incomeTaxSource === 'entered' ? 'entered' : 'estimated',
    incomeTax: financeNumber(source.incomeTax),
    profit: financeNumber(source.profit),
    profitPercent: financeNumber(source.profitPercent),
    pendingCostOrderCount: num(source.pendingCostOrderCount),
    planPendingCostOrderCount: num(source.planPendingCostOrderCount),
    ...(source.pendingCostDeltas && typeof source.pendingCostDeltas === 'object'
      ? { pendingCostDeltas: normalizeDeltas(source.pendingCostDeltas) }
      : {}),
  };
}

function normalizeMonth(value: unknown): IncomeStatementMonth {
  const source = record(value);
  return {
    month: text(source.month),
    isCurrent: bool(source.isCurrent),
    isFuture: bool(source.isFuture),
    actual: normalizeLine(source.actual),
    forecast: normalizeLine(source.forecast),
  };
}

function normalizeCustomerTotals(value: unknown): IncomeStatementCustomerTotals {
  const source = record(value);
  return {
    orderCount: num(source.orderCount),
    revenue: num(source.revenue),
    orderCosts: financeNumber(source.orderCosts),
    margin: financeNumber(source.margin),
    marginPercent: financeNumber(source.marginPercent),
    pendingCostOrderCount: num(source.pendingCostOrderCount),
  };
}

function normalizeCustomerOrderMoney(value: unknown): IncomeStatementCustomerOrderMoney | null {
  if (!value || typeof value !== 'object') return null;
  const source = record(value);
  return {
    revenue: num(source.revenue),
    orderCosts: financeNumber(source.orderCosts),
    margin: financeNumber(source.margin),
  };
}

function normalizeCustomerOrder(value: unknown): IncomeStatementCustomerOrder {
  const source = record(value);
  return {
    id: text(source.id),
    orderNumber: text(source.orderNumber),
    displayOrderNumber: text(source.displayOrderNumber, text(source.orderNumber)),
    status: text(source.status),
    plannedDate: nullableText(source.plannedDate),
    forecastMonths: Array.isArray(source.forecastMonths)
      ? source.forecastMonths.filter((month: unknown): month is string => typeof month === 'string' && /^\d{4}-\d{2}$/.test(month))
      : [],
    reviewState: text(source.reviewState, 'REVIEW') as IncomeStatementReviewState,
    requiresReview: bool(source.requiresReview),
    inActual: bool(source.inActual),
    inForecast: bool(source.inForecast),
    awaitingSettlement: bool(source.awaitingSettlement),
    costPending: bool(source.costPending),
    actualCostPending: bool(source.actualCostPending),
    actual: normalizeCustomerOrderMoney(source.actual),
    forecast: normalizeCustomerOrderMoney(source.forecast),
  };
}

function normalizeAwaitingSettlementSummary(value: unknown): IncomeStatementAwaitingSettlementSummary {
  const source = record(value);
  return {
    orderCount: num(source.orderCount),
    revenue: num(source.revenue),
  };
}

function normalizeOutsidePlanOrder(value: unknown): IncomeStatementOutsidePlanOrder {
  const source = record(value);
  return {
    id: text(source.id),
    orderNumber: text(source.orderNumber),
    displayOrderNumber: text(source.displayOrderNumber, text(source.orderNumber)),
    customerName: text(source.customerName, '—'),
    plannedDate: nullableText(source.plannedDate),
    reviewReasons: Array.isArray(source.reviewReasons) ? source.reviewReasons.filter((reason: unknown) => typeof reason === 'string') : [],
    revenue: num(source.revenue),
  };
}

function normalizeOutsidePlan(value: unknown): IncomeStatementOutsidePlan {
  const source = record(value);
  return {
    orderCount: num(source.orderCount),
    revenue: num(source.revenue),
    orders: Array.isArray(source.orders) ? source.orders.map(normalizeOutsidePlanOrder) : [],
  };
}

function normalizeCustomer(value: unknown): IncomeStatementCustomer {
  const source = record(value);
  return {
    key: text(source.key, 'none'),
    id: nullableText(source.id),
    name: text(source.name, '—'),
    taxId: nullableText(source.taxId),
    address: nullableText(source.address),
    actual: normalizeCustomerTotals(source.actual),
    forecast: normalizeCustomerTotals(source.forecast),
    orders: Array.isArray(source.orders) ? source.orders.map(normalizeCustomerOrder) : [],
  };
}

function normalizeForecastOrderTotals(value: unknown): ForecastVsActualOrderTotals {
  const source = record(value);
  return {
    orderCount: num(source.orderCount),
    revenue: num(source.revenue),
    totalCost: financeNumber(source.totalCost),
    margin: financeNumber(source.margin),
    marginPercent: financeNumber(source.marginPercent),
    pendingCostOrderCount: num(source.pendingCostOrderCount),
  };
}

function normalizeForecastBasis(value: unknown): ForecastVsActualBasis {
  const source = record(value);
  const purchases = record(source.purchases);
  return {
    orders: normalizeForecastOrderTotals(source.orders),
    purchases: { count: num(purchases.count), amountPLN: num(purchases.amountPLN), pendingCostCount: num(purchases.pendingCostCount) },
  };
}

function normalizeForecastMoney(value: unknown): ForecastVsActualMoney | null {
  if (!value || typeof value !== 'object') return null;
  const source = record(value);
  return {
    revenue: financeNumber(source.revenue),
    totalCost: financeNumber(source.totalCost),
    margin: financeNumber(source.margin),
  };
}

function normalizeForecastOrder(value: unknown): ForecastVsActualOrder {
  const source = record(value);
  const customer = source.customer && typeof source.customer === 'object' ? record(source.customer) : null;
  const accountManager = source.accountManager && typeof source.accountManager === 'object' ? record(source.accountManager) : null;
  const unissued = source.unissued && typeof source.unissued === 'object' ? record(source.unissued) : null;
  const planned = normalizeForecastMoney(source.planned);
  return {
    id: text(source.id),
    orderNumber: text(source.orderNumber),
    displayOrderNumber: text(source.displayOrderNumber, text(source.orderNumber)),
    status: text(source.status),
    customer: customer ? { id: nullableText(customer.id), name: text(customer.name, '—') } : null,
    accountManager: accountManager ? { id: nullableText(accountManager.id), name: text(accountManager.name, '—') } : null,
    customerPaymentStatus: source.customerPaymentStatus === 'PAID' ? 'PAID' : 'PENDING',
    paymentMode: source.paymentMode === 'FACTORING' || source.paymentMode === 'PREPAYMENT' ? source.paymentMode : 'TERM',
    month: nullableText(source.month),
    revenue: num(source.revenue),
    totalCost: financeNumber(source.totalCost),
    margin: financeNumber(source.margin),
    marginPercent: financeNumber(source.marginPercent),
    costPending: bool(source.costPending),
    ...(source.pendingDeltas && typeof source.pendingDeltas === 'object'
      ? { pendingDeltas: normalizeDeltas(source.pendingDeltas) }
      : {}),
    deliveryShare: source.deliveryShare == null ? null : financeNumber(source.deliveryShare),
    deliverySource: FORECAST_DELIVERY_SOURCES.includes(source.deliverySource) ? source.deliverySource : null,
    knownCost: financeNumber(source.knownCost),
    unissued: unissued
      ? { averageCostPLN: financeNumber(unissued.averageCostPLN), costUnknown: bool(unissued.costUnknown) }
      : null,
    issuedOverInvoiceCostPLN: financeNumber(source.issuedOverInvoiceCostPLN),
    planned: planned ? { ...planned, snapshotAt: nullableText(record(source.planned).snapshotAt) } : null,
    delta: normalizeForecastMoney(source.delta),
  };
}

export function normalizeForecastVsActual(value: unknown): ForecastVsActual {
  const source = record(value);
  const totals = record(source.totals);
  const orders = record(source.orders);
  return {
    months: Array.isArray(source.months) ? source.months.map((entry: unknown) => {
      const month = record(entry);
      return {
        month: text(month.month),
        forecast: normalizeForecastBasis(month.forecast),
        actual: normalizeForecastBasis(month.actual),
      };
    }) : [],
    totals: { forecast: normalizeForecastBasis(totals.forecast), actual: normalizeForecastBasis(totals.actual) },
    issuedOverInvoice: {
      orderCount: num(record(source.issuedOverInvoice).orderCount),
      costPLN: num(record(source.issuedOverInvoice).costPLN),
    },
    orders: {
      forecast: Array.isArray(orders.forecast) ? orders.forecast.map(normalizeForecastOrder) : [],
      actual: Array.isArray(orders.actual) ? orders.actual.map(normalizeForecastOrder) : [],
    },
  };
}

export function normalizeIncomeStatement(value: unknown): IncomeStatementResponse {
  const source = record(value);
  const totals = record(source.totals);
  return {
    purchases: Array.isArray(source.purchases) ? source.purchases.map((value: unknown) => {
      const row = record(value);
      const draftSource = row.draft == null ? null : record(row.draft);
      const draft = draftSource ? {
        sourceOrderId: nullableText(draftSource.sourceOrderId),
        sourceOrderNumber: nullableText(draftSource.sourceOrderNumber),
        costPending: bool(draftSource.costPending),
        pendingReasons: Array.isArray(draftSource.pendingReasons) ? draftSource.pendingReasons.map((reason: unknown) => text(reason)).filter(Boolean) : [],
      } : null;
      const kind = ['PLAN', 'CORRECTION', 'WITHDRAWAL'].includes(text(row.kind)) ? text(row.kind) as IncomeStatementPurchase['kind']
        : bool(row.actual) ? 'INVOICE' : 'PLAN';
      return { kind, supplierOrderId: text(row.supplierOrderId), orderNumber: text(row.orderNumber), supplierName: text(row.supplierName),
        invoiceId: nullableText(row.invoiceId), documentNumber: nullableText(row.documentNumber), month: text(row.month), actual: bool(row.actual),
        contractCurrency: text(row.contractCurrency), contractNet: num(row.contractNet), contractNetPLN: num(row.contractNetPLN), contractRate: financeNumber(row.contractRate),
        invoiceCurrency: nullableText(row.invoiceCurrency), invoiceNet: financeNumber(row.invoiceNet), invoiceRate: financeNumber(row.invoiceRate),
        netPLN: draft ? financeNumber(row.netPLN) : num(row.netPLN), extraCostPLN: num(row.extraCostPLN), draft };
    }) : [],
    from: text(source.from),
    to: text(source.to),
    currentMonth: text(source.currentMonth),
    months: Array.isArray(source.months) ? source.months.map(normalizeMonth) : [],
    totals: {
      actual: normalizeLine(totals.actual),
      forecast: normalizeLine(totals.forecast),
    },
    customers: Array.isArray(source.customers) ? source.customers.map(normalizeCustomer) : [],
    awaitingSettlementSummary: normalizeAwaitingSettlementSummary(source.awaitingSettlementSummary),
    outsidePlan: normalizeOutsidePlan(source.outsidePlan),
    forecastVsActual: source.forecastVsActual == null ? null : normalizeForecastVsActual(source.forecastVsActual),
  };
}

export async function fetchIncomeStatement(from: string, to: string): Promise<IncomeStatementResponse> {
  const response = await api.get('/reports/income-statement', { params: { from, to } });
  return normalizeIncomeStatement(response.data);
}

/** „Przekrój sprzedaży” as an Excel file (the table the page shows; ADMIN only). */
export async function exportSalesPivot(table: PivotExportTable): Promise<{ blob: Blob; filename: string }> {
  const response = await api.post('/reports/sales-pivot/export', table, { responseType: 'blob' });
  const disposition = response.headers?.['content-disposition'];
  const match = typeof disposition === 'string' ? disposition.match(/filename="?([^";]+)"?/i) : null;
  // The header is unreadable across origins (local harness): same name as the backend gives.
  return { blob: response.data, filename: match ? match[1] : `przekroj-sprzedazy-${new Date().toISOString().slice(0, 10)}.xlsx` };
}
